import { type FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Blaze, Box, Button } from '@dhl-ui/components'
import { LoadingIndicator } from '@dhl/components'
import { SmartDeliveryOptionIllustration } from '@dhl-ui/illustrations'
import { useWizardContext, useSetTitle } from '~/setup/model/context.js'
import { steps } from '~/setup/model/steps.js'
import { useGetProfileLastAddedAddress } from '~/profile/model/profile/hooks.js'

export const DeliveryPreferenceIntro: FC = () => {
  const { t } = useTranslation()
  const { goToStep, setCurrentStepCanContinue, setIsLoading } = useWizardContext()

  useSetTitle(t('portal.profileSetup.deliveryPreference.pageTitle'))
  const { data, data: lastAddedAddress, isFetching } = useGetProfileLastAddedAddress()

  const hasAddress = !!lastAddedAddress?.addressId

  const stepDescriptionKey = hasAddress
    ? 'portal.profileSetup.deliveryPreference.stepDescription'
    : 'portal.profileSetup.deliveryPreference.stepDescriptionNoAddress'

  const handleCreateAddressClick = () => goToStep(steps.address)

  const isLoading = isFetching && !data

  useEffect(() => {
    if (isLoading || isFetching || !hasAddress) {
      setCurrentStepCanContinue(false)
    } else {
      setCurrentStepCanContinue(true)
    }
    setIsLoading(isLoading)
  }, [setCurrentStepCanContinue, setIsLoading, hasAddress, isFetching, data, isLoading])

  if (isLoading) {
    return <LoadingIndicator sx={{ height: '100%' }} />
  }

  return (
    <Box data-testid="profile-setup-delivery-preference-intro-text">
      <Blaze
        data-testid="setup-wizard-step-blaze"
        illustration={<SmartDeliveryOptionIllustration />}
        title={t('portal.profileSetup.deliveryPreference.stepTitle')}
        description={t(stepDescriptionKey)}
        sx={{ m: '20vh auto 0 auto' }}
      />
      {!hasAddress && (
        <Box display="flex" justifyContent="center">
          <Button sx={{ mt: 2 }} variant="contained" onClick={handleCreateAddressClick}>
            {t('portal.profileSetup.deliveryPreference.addAddressButton')}
          </Button>
        </Box>
      )}
    </Box>
  )
}
